import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { Recycle, Trash2, Filter, PackageCheck, PackageX, BarChart3, ArrowDownUp } from 'lucide-react';
import { formatNumber, formatDecimal } from '../utils/formatters';
interface BarrelTrackingProps {
  timeRange?: 'day' | 'week' | 'month' | 'year' | 'custom';
}
export const WaschanlageBarrelTracking: React.FC<BarrelTrackingProps> = ({
  timeRange = 'day'
}) => {
  // Simulate data based on time range - in a real app, this would come from an API or props
  const getMultiplier = () => {
    switch (timeRange) {
      case 'day':
        return 1;
      case 'week':
        return 7;
      case 'month':
        return 30;
      case 'year':
        return 365;
      default:
        return 1;
    }
  };
  const multiplier = getMultiplier();
  // Base numbers for a day
  const baseProcessed = 350;
  const baseReused = 280;
  const baseDerusted = 210;
  const baseScrapped = 70;
  const baseQualityA = 245;
  const baseQualityB = 105;
  // Calculate values based on time range
  const totalProcessed = baseProcessed * multiplier;
  const reusedBarrels = baseReused * multiplier;
  const derustedBarrels = baseDerusted * multiplier;
  const scrappedBarrels = baseScrapped * multiplier;
  const qualityABarrels = baseQualityA * multiplier;
  const qualityBBarrels = baseQualityB * multiplier;
  // Calculate percentages
  const reusedPercentage = Math.round(reusedBarrels / totalProcessed * 100);
  const derustedPercentage = Math.round(derustedBarrels / totalProcessed * 100);
  const scrappedPercentage = Math.round(scrappedBarrels / totalProcessed * 100);
  const qualityAPercentage = Math.round(qualityABarrels / reusedBarrels * 100);
  const qualityBPercentage = Math.round(qualityBBarrels / reusedBarrels * 100);
  // Data for pie chart
  const processingData = [{
    name: 'Wiederzugeführt',
    value: reusedBarrels - derustedBarrels,
    color: '#4ade80'
  }, {
    name: 'Entrostet',
    value: derustedBarrels,
    color: '#60a5fa'
  }, {
    name: 'Verschrottet',
    value: scrappedBarrels,
    color: '#f87171'
  }];
  // Data for quality chart
  const qualityData = [{
    name: 'A-Ware',
    value: qualityABarrels,
    color: '#60a5fa'
  }, {
    name: 'B-Ware',
    value: qualityBBarrels,
    color: '#fbbf24'
  }];
  // Data for bar chart - historical data (simulated)
  const historicalData = [{
    name: 'Mo',
    reused: 275,
    derusted: 200,
    scrapped: 65,
    qualityA: 240,
    qualityB: 35
  }, {
    name: 'Di',
    reused: 290,
    derusted: 220,
    scrapped: 60,
    qualityA: 250,
    qualityB: 40
  }, {
    name: 'Mi',
    reused: 285,
    derusted: 215,
    scrapped: 75,
    qualityA: 245,
    qualityB: 40
  }, {
    name: 'Do',
    reused: 280,
    derusted: 210,
    scrapped: 70,
    qualityA: 240,
    qualityB: 40
  }, {
    name: 'Fr',
    reused: 295,
    derusted: 225,
    scrapped: 65,
    qualityA: 255,
    qualityB: 40
  }, {
    name: 'Sa',
    reused: 260,
    derusted: 190,
    scrapped: 80,
    qualityA: 220,
    qualityB: 40
  }, {
    name: 'So',
    reused: 250,
    derusted: 180,
    scrapped: 70,
    qualityA: 215,
    qualityB: 35
  }];
  // Custom tooltip for the bar chart
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-gray-800 p-3 border border-gray-700 rounded-lg shadow-lg" data-id="element-1056">
          <p className="text-gray-300 font-medium" data-id="element-1057">{label}</p>
          {payload.map((entry: any, index: number) => <p key={`item-${index}`} style={{
          color: entry.color
        }} className="text-sm" data-id="element-1058">
              {entry.name}: {entry.value} Fässer
            </p>)}
        </div>;
    }
    return null;
  };
  return <div className="space-y-6" data-id="element-1059">
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-1060">
        <div className="flex items-center gap-2 mb-4" data-id="element-1061">
          <BarChart3 className="text-blue-400" size={24} data-id="element-1062" />
          <h3 className="text-xl font-medium text-gray-200" data-id="element-1063">
            Fassverarbeitung - Übersicht
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-1064">
          <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50" data-id="element-1065">
            <div className="flex items-center gap-2 mb-2" data-id="element-1066">
              <PackageCheck className="text-green-400" size={18} data-id="element-1067" />
              <h5 className="text-md font-medium text-gray-200" data-id="element-1068">
                Verarbeitete Fässer
              </h5>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-1069">
              {formatNumber(totalProcessed)}
            </p>
            <div className="mt-1 text-sm text-gray-400" data-id="element-1070">
              {timeRange === 'day' ? 'Heute' : timeRange === 'week' ? 'Diese Woche' : timeRange === 'month' ? 'Diesen Monat' : 'Dieses Jahr'}
            </div>
            <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-1071">
              <div className="h-1.5 rounded-full bg-blue-500 w-full" data-id="element-1072"></div>
            </div>
          </div>
          <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50" data-id="element-1073">
            <div className="flex items-center gap-2 mb-2" data-id="element-1074">
              <Recycle className="text-green-400" size={18} data-id="element-1075" />
              <h5 className="text-md font-medium text-gray-200" data-id="element-1076">
                Wiederzugeführt
              </h5>
            </div>
            <div className="flex items-baseline justify-between" data-id="element-1077">
              <p className="text-2xl font-bold text-white" data-id="element-1078">
                {formatNumber(reusedBarrels)}
              </p>
              <p className="text-lg font-medium text-green-400" data-id="element-1079">
                {reusedPercentage}%
              </p>
            </div>
            <div className="mt-1 text-sm text-gray-400" data-id="element-1080">
              Fässer zurück in den Prozess
            </div>
            <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-1081">
              <div className="h-1.5 rounded-full bg-green-500" style={{
              width: `${reusedPercentage}%`
            }} data-id="element-1082"></div>
            </div>
          </div>
          <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50" data-id="element-1083">
            <div className="flex items-center gap-2 mb-2" data-id="element-1084">
              <Trash2 className="text-red-400" size={18} data-id="element-1085" />
              <h5 className="text-md font-medium text-gray-200" data-id="element-1086">
                Verschrottet
              </h5>
            </div>
            <div className="flex items-baseline justify-between" data-id="element-1087">
              <p className="text-2xl font-bold text-white" data-id="element-1088">
                {formatNumber(scrappedBarrels)}
              </p>
              <p className="text-lg font-medium text-red-400" data-id="element-1089">
                {scrappedPercentage}%
              </p>
            </div>
            <div className="mt-1 text-sm text-gray-400" data-id="element-1090">
              Fässer entsorgt/verschrottet
            </div>
            <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-1091">
              <div className="h-1.5 rounded-full bg-red-500" style={{
              width: `${scrappedPercentage}%`
            }} data-id="element-1092"></div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1093">
          <div className="bg-gray-700/30 p-4 rounded-xl border border-gray-600/30" data-id="element-1094">
            <div className="flex items-center justify-between mb-3" data-id="element-1095">
              <div className="flex items-center gap-2" data-id="element-1096">
                <Filter className="text-blue-400" size={18} data-id="element-1097" />
                <h5 className="text-md font-medium text-gray-200" data-id="element-1098">
                  Fassverarbeitung
                </h5>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between" data-id="element-1099">
              <div className="h-64 w-full md:w-1/2" data-id="element-1100">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1101">
                  <PieChart data-id="element-1102">
                    <Pie data={processingData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-1103">
                      {processingData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1104" />)}
                    </Pie>
                    <Tooltip formatter={(value: number) => [`${value} Fässer`, '']} contentStyle={{
                    backgroundColor: '#1f2937',
                    borderColor: '#374151',
                    borderRadius: '0.5rem'
                  }} itemStyle={{
                    color: '#e5e7eb'
                  }} data-id="element-1105" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4 w-full md:w-1/2" data-id="element-1106">
                <div data-id="element-1107">
                  <div className="flex items-center justify-between mb-1" data-id="element-1108">
                    <div className="flex items-center gap-2" data-id="element-1109">
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-1110"></div>
                      <span className="text-gray-300" data-id="element-1111">Wiederzugeführt</span>
                    </div>
                    <span className="font-medium text-white" data-id="element-1112">
                      {formatNumber(reusedBarrels - derustedBarrels)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-1113">
                    Direkt zurück in den Prozess
                  </div>
                </div>
                <div data-id="element-1114">
                  <div className="flex items-center justify-between mb-1" data-id="element-1115">
                    <div className="flex items-center gap-2" data-id="element-1116">
                      <div className="w-3 h-3 rounded-full bg-blue-500" data-id="element-1117"></div>
                      <span className="text-gray-300" data-id="element-1118">Entrostet</span>
                    </div>
                    <span className="font-medium text-white" data-id="element-1119">
                      {formatNumber(derustedBarrels)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-1120">
                    Nach Entrostung zurück in den Prozess
                  </div>
                </div>
                <div data-id="element-1121">
                  <div className="flex items-center justify-between mb-1" data-id="element-1122">
                    <div className="flex items-center gap-2" data-id="element-1123">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-1124"></div>
                      <span className="text-gray-300" data-id="element-1125">Verschrottet</span>
                    </div>
                    <span className="font-medium text-white" data-id="element-1126">
                      {formatNumber(scrappedBarrels)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-1127">
                    Zu stark beschädigt für Wiederverwendung
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-700/30 p-4 rounded-xl border border-gray-600/30" data-id="element-1128">
            <div className="flex items-center justify-between mb-3" data-id="element-1129">
              <div className="flex items-center gap-2" data-id="element-1130">
                <ArrowDownUp className="text-yellow-400" size={18} data-id="element-1131" />
                <h5 className="text-md font-medium text-gray-200" data-id="element-1132">
                  Qualitätsverteilung
                </h5>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between" data-id="element-1133">
              <div className="h-64 w-full md:w-1/2" data-id="element-1134">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1135">
                  <PieChart data-id="element-1136">
                    <Pie data={qualityData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-1137">
                      {qualityData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1138" />)}
                    </Pie>
                    <Tooltip formatter={(value: number) => [`${value} Fässer`, '']} contentStyle={{
                    backgroundColor: '#1f2937',
                    borderColor: '#374151',
                    borderRadius: '0.5rem'
                  }} itemStyle={{
                    color: '#e5e7eb'
                  }} data-id="element-1139" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4 w-full md:w-1/2" data-id="element-1140">
                <div data-id="element-1141">
                  <div className="flex items-center justify-between mb-1" data-id="element-1142">
                    <div className="flex items-center gap-2" data-id="element-1143">
                      <div className="w-3 h-3 rounded-full bg-blue-500" data-id="element-1144"></div>
                      <span className="text-gray-300" data-id="element-1145">A-Ware</span>
                    </div>
                    <span className="font-medium text-white" data-id="element-1146">
                      {formatNumber(qualityABarrels)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-1147">
                    Erstklassige Qualität ({qualityAPercentage}% der
                    wiederzugeführten)
                  </div>
                </div>
                <div data-id="element-1148">
                  <div className="flex items-center justify-between mb-1" data-id="element-1149">
                    <div className="flex items-center gap-2" data-id="element-1150">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1151"></div>
                      <span className="text-gray-300" data-id="element-1152">B-Ware</span>
                    </div>
                    <span className="font-medium text-white" data-id="element-1153">
                      {formatNumber(qualityBBarrels)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-1154">
                    Zweitklassige Qualität ({qualityBPercentage}% der
                    wiederzugeführten)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-1155">
        <div className="flex items-center gap-2 mb-4" data-id="element-1156">
          <Filter className="text-blue-400" size={24} data-id="element-1157" />
          <h3 className="text-xl font-medium text-gray-200" data-id="element-1158">
            Detaillierte Fassdaten
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-1159">
          <table className="w-full text-sm" data-id="element-1160">
            <thead data-id="element-1161">
              <tr className="border-b border-gray-600" data-id="element-1162">
                <th className="text-left py-2 pr-4 text-gray-300 font-medium" data-id="element-1163">
                  Kategorie
                </th>
                <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-1164">
                  Anzahl
                </th>
                <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-1165">
                  Prozent
                </th>
                <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-1166">
                  Ø pro Tag
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1167">
              <tr className="border-b border-gray-700/50" data-id="element-1168">
                <td className="py-2 pr-4" data-id="element-1169">
                  <div className="flex items-center gap-2" data-id="element-1170">
                    <PackageCheck className="text-blue-400" size={16} data-id="element-1171" />
                    <span className="font-medium text-gray-200" data-id="element-1172">
                      Verarbeitete Fässer (Gesamt)
                    </span>
                  </div>
                </td>
                <td className="text-right py-2 px-2 font-medium text-white" data-id="element-1173">
                  {formatNumber(totalProcessed)}
                </td>
                <td className="text-right py-2 px-2 text-gray-300" data-id="element-1174">100%</td>
                <td className="text-right py-2 px-2 text-gray-300" data-id="element-1175">
                  {formatNumber(baseProcessed)}
                </td>
              </tr>
              <tr className="border-b border-gray-700/50" data-id="element-1176">
                <td className="py-2 pr-4" data-id="element-1177">
                  <div className="flex items-center gap-2" data-id="element-1178">
                    <Recycle className="text-green-400" size={16} data-id="element-1179" />
                    <span className="font-medium text-gray-200" data-id="element-1180">
                      Wiederzugeführte Fässer
                    </span>
                  </div>
                </td>
                <td className="text-right py-2 px-2 font-medium text-white" data-id="element-1181">
                  {formatNumber(reusedBarrels)}
                </td>
                <td className="text-right py-2 px-2 text-green-400" data-id="element-1182">
                  {reusedPercentage}%
                </td>
                <td className="text-right py-2 px-2 text-gray-300" data-id="element-1183">
                  {formatNumber(baseReused)}
                </td>
              </tr>
              <tr className="border-b border-gray-700/50" data-id="element-1184">
                <td className="py-2 pr-4 pl-6" data-id="element-1185">
                  <div className="flex items-center gap-2" data-id="element-1186">
                    <Filter className="text-blue-400" size={16} data-id="element-1187" />
                    <span className="text-gray-200" data-id="element-1188">Davon entrostet</span>
                  </div>
                </td>
                <td className="text-right py-2 px-2 text-white" data-id="element-1189">
                  {formatNumber(derustedBarrels)}
                </td>
                <td className="text-right py-2 px-2 text-blue-400" data-id="element-1190">
                  {Math.round(derustedBarrels / totalProcessed * 100)}%
                </td>
                <td className="text-right py-2 px-2 text-gray-300" data-id="element-1191">
                  {formatNumber(baseDerusted)}
                </td>
              </tr>
              <tr className="border-b border-gray-700/50" data-id="element-1192">
                <td className="py-2 pr-4 pl-6" data-id="element-1193">
                  <div className="flex items-center gap-2" data-id="element-1194">
                    <PackageCheck className="text-blue-400" size={16} data-id="element-1195" />
                    <span className="text-gray-200" data-id="element-1196">A-Ware</span>
                  </div>
                </td>
                <td className="text-right py-2 px-2 text-white" data-id="element-1197">
                  {formatNumber(qualityABarrels)}
                </td>
                <td className="text-right py-2 px-2 text-blue-400" data-id="element-1198">
                  {qualityAPercentage}% der wiederzugeführten
                </td>
                <td className="text-right py-2 px-2 text-gray-300" data-id="element-1199">
                  {formatNumber(baseQualityA)}
                </td>
              </tr>
              <tr className="border-b border-gray-700/50" data-id="element-1200">
                <td className="py-2 pr-4 pl-6" data-id="element-1201">
                  <div className="flex items-center gap-2" data-id="element-1202">
                    <PackageX className="text-yellow-400" size={16} data-id="element-1203" />
                    <span className="text-gray-200" data-id="element-1204">B-Ware</span>
                  </div>
                </td>
                <td className="text-right py-2 px-2 text-white" data-id="element-1205">
                  {formatNumber(qualityBBarrels)}
                </td>
                <td className="text-right py-2 px-2 text-yellow-400" data-id="element-1206">
                  {qualityBPercentage}% der wiederzugeführten
                </td>
                <td className="text-right py-2 px-2 text-gray-300" data-id="element-1207">
                  {formatNumber(baseQualityB)}
                </td>
              </tr>
              <tr className="border-b border-gray-700/50" data-id="element-1208">
                <td className="py-2 pr-4" data-id="element-1209">
                  <div className="flex items-center gap-2" data-id="element-1210">
                    <Trash2 className="text-red-400" size={16} data-id="element-1211" />
                    <span className="font-medium text-gray-200" data-id="element-1212">
                      Verschrottete Fässer
                    </span>
                  </div>
                </td>
                <td className="text-right py-2 px-2 font-medium text-white" data-id="element-1213">
                  {formatNumber(scrappedBarrels)}
                </td>
                <td className="text-right py-2 px-2 text-red-400" data-id="element-1214">
                  {scrappedPercentage}%
                </td>
                <td className="text-right py-2 px-2 text-gray-300" data-id="element-1215">
                  {formatNumber(baseScrapped)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};